import { UPLOAD_CHUNK_SIZE } from 'constants/index';
import {
  directUploadComplete,
  directUploadStart,
  fetchPreSignedUrl,
  putPreSignedUrl,
  videoTranscode,
} from 'service/api/UploadVideoApiService';
import { accountStore } from 'store/accountStore';
import {
  DirectUploadStartResponseInterface,
  UploadedPartsInterface,
  UploadVideoInterface,
} from 'type/uploadVideoType';
import { uploadVideoStore } from '../store/uploadVideoStore';

class UploadVideoService {
  async uploadVideoFiles(
    needUploadVideoList: UploadVideoInterface[],
    autoTranscode: boolean,
    contentId?: number
  ): Promise<boolean> {
    await Promise.all(
      needUploadVideoList.map(async (videoFile: UploadVideoInterface) => {
        try {
          if (!this.validateVideoFile(videoFile)) {
            return;
          }
          videoFile.state = 'uploading';
          const { uploadId, contentId: contentIdFromCreate } = await this.createUploadVideo(
            videoFile,
            contentId
          );
          const preSignedUrls = await this.fetchPreSignedUrls(
            videoFile,
            uploadId,
            contentIdFromCreate
          );
          const uploadedParts = await this.uploadVideoFilesByPreSignedUrl(videoFile, preSignedUrls);

          await directUploadComplete({
            directVideoUploadFile: videoFile,
            contentId: contentIdFromCreate,
            uploadId: uploadId,
            uploadedParts,
          });

          if (autoTranscode) {
            await videoTranscode(contentIdFromCreate);
          }
          videoFile.state = 'uploaded';
        } catch (error) {
          if (videoFile) {
            videoFile.state = 'failed';
          }
          throw error;
        }
      })
    );

    return true;
  }

  private async createUploadVideo(
    videoFile: UploadVideoInterface,
    contentId?: number
  ): Promise<DirectUploadStartResponseInterface> {
    const { title, name, type, size, synopsis } = videoFile;
    const response = await directUploadStart(
      title || name,
      type!,
      size!,
      synopsis || '',
      accountStore.getAccount()?.username || '',
      contentId
    );

    return {
      uploadId: response.data.uploadId,
      contentId: response.data.contentId,
    };
  }

  private async fetchPreSignedUrls(
    videoFile: UploadVideoInterface,
    uploadId: string,
    contentId: number
  ): Promise<string[]> {
    const totalParts = Math.ceil(videoFile.size! / UPLOAD_CHUNK_SIZE);
    const preSignedUrls = await Promise.all(
      Array.from({ length: totalParts }, (_, partNumber) =>
        fetchPreSignedUrl({
          directVideoUploadFile: videoFile,
          contentId,
          uploadId: uploadId,
          partNumber: partNumber + 1,
        })
      )
    );
    return preSignedUrls.map(urlResponse => urlResponse.data as unknown as string);
  }

  private async uploadVideoFilesByPreSignedUrl(
    videoFile: UploadVideoInterface,
    preSignedUrls: string[]
  ): Promise<UploadedPartsInterface[]> {
    const { size, originFileObj } = videoFile;
    const totalParts = Math.ceil(size! / UPLOAD_CHUNK_SIZE);
    const chunkProgressArray = new Array(totalParts).fill(0);
    const uploadedParts: UploadedPartsInterface[] = [];
    let lastUploadedBytes = 0;
    let lastTime = performance.now();

    await Promise.all(
      preSignedUrls.map(async (preSignedUrl, partNumber) => {
        const start = partNumber * UPLOAD_CHUNK_SIZE;
        const end = Math.min((partNumber + 1) * UPLOAD_CHUNK_SIZE, size!);
        const chunk = originFileObj?.slice(start, end);

        const preSignedUrlResponse = await putPreSignedUrl({
          preSignedUrl,
          chunk: chunk as Blob,
          onChunkProgress: progress => {
            chunkProgressArray[partNumber] = progress;
            const uploadedBytes = chunkProgressArray.reduce((acc, val) => acc + val, 0);
            const overallProgress = (uploadedBytes / size!) * 100;
            const currentTime = performance.now();
            const elapsedSeconds = (currentTime - lastTime) / 1000;

            const bytesUploadedSinceLastCheck = uploadedBytes - lastUploadedBytes;
            const uploadSpeedBytesPerSecond = bytesUploadedSinceLastCheck / elapsedSeconds;

            uploadVideoStore.updateProgress(
              videoFile.uid,
              overallProgress,
              uploadSpeedBytesPerSecond
            );

            lastUploadedBytes = uploadedBytes;
            lastTime = currentTime;
          },
        });
        uploadedParts.push({
          ETag: preSignedUrlResponse.headers.etag,
          PartNumber: partNumber + 1,
        });
      })
    );
    return uploadedParts;
  }

  private validateVideoFile(videoFile: UploadVideoInterface): boolean {
    const { size, name, type } = videoFile;
    return !!size && !!name && !!type;
  }
}

export const uploadVideoService = new UploadVideoService();
